// src/services/intentionsService.ts
import { supabase } from '../supabase/config';
import { TeachingIntention, TeachingPreference } from '../types/intentions';
import { emailService } from './emailService';

// Imported intentions (raw spreadsheet shape) store human-readable labels under
// different keys than the app's coded shape. These helpers read the coded key
// first (app-submitted rows are unaffected), then fall back to normalising the
// raw label so reports show the real answer instead of a default.
function normalizeIntentionToReturn(d: Record<string, any>): 'yes' | 'no' | 'uncertain' {
  const coded = d?.intentionToReturn;
  if (coded === 'yes' || coded === 'no' || coded === 'uncertain') return coded;
  const raw = String(d?.returning ?? '').toLowerCase();
  if (raw.includes('yes')) return 'yes';
  if (raw.includes('no')) return 'no';
  return 'uncertain';
}

function normalizeEmploymentType(d: Record<string, any>): TeachingIntention['currentEmploymentType'] {
  const coded = d?.currentEmploymentType;
  if (coded) return coded;
  const raw = String(d?.employmentType ?? '').toLowerCase();
  if (raw.includes('ongoing')) return 'ongoing';
  if (raw.includes('fixed') || raw.includes('contract')) return 'fixed-term';
  if (raw.includes('casual')) return 'casual';
  if (raw.includes('part')) return 'part-time';
  if (raw.includes('full')) return 'full-time';
  return 'full-time';
}

// The submission form writes a teacher's ranked nominations to the structured
// `preferences` column — { curriculumAreas, subjects, ruahSubjects } — and leaves
// `intentions.teachingPreferences` empty. Rows imported from the HR spreadsheet do
// the exact opposite. Every consumer of a TeachingIntention reads
// `teachingPreferences` (the teacher assignment modal, auto-assign, the preference
// match report, the staff preferences drawer), so the two shapes have to be
// reconciled here, at the single point where rows are mapped — otherwise every
// app-submitted intention reads as "No teaching preferences recorded" (issue #167).
function resolveTeachingPreferences(
  intentionData: Record<string, any>,
  prefsColumn?: any
): TeachingPreference[] {
  // Imported rows already carry the coded shape; never second-guess them.
  const existing = intentionData?.teachingPreferences;
  if (Array.isArray(existing) && existing.length > 0) return existing;

  const prefs = prefsColumn || intentionData?.preferences;
  if (!prefs) return [];

  const asArray = (v: any): any[] => (Array.isArray(v) ? v : []);
  const derived: TeachingPreference[] = [];

  // Specific subject nominations first — they name an actual class, and the
  // submission form uses the same naming as the grid ("BIOLOGY12"), so these
  // match exactly.
  const subjects = asArray(prefs.subjects);
  subjects.forEach((s, i) => {
    const name = s?.subjectName || s?.subjectId;
    if (name) derived.push({ rank: s?.rank ?? i + 1, learningArea: '', specificSubject: name });
  });

  // RUAH nominations are captured separately because they sit on their own grid.
  // Their names never collide with a main-grid subject, so they keep their own
  // 1..N ranking rather than being pushed behind the list above.
  asArray(prefs.ruahSubjects).forEach((s, i) => {
    const name = s?.subjectName || s?.subjectId;
    if (name) derived.push({ rank: s?.rank ?? i + 1, learningArea: '', specificSubject: name });
  });

  // Ranked learning areas are the broader nomination, so they rank after the
  // specific subjects instead of competing with them for rank 1.
  asArray(prefs.curriculumAreas).forEach((a, i) => {
    const name = a?.areaName;
    if (name) {
      derived.push({
        rank: subjects.length + (a?.rank ?? i + 1),
        learningArea: name,
        specificSubject: '',
      });
    }
  });

  return derived;
}

// Same story for RUAH: the legacy flat string[] is only written by the
// spreadsheet importer, so derive it from the structured column when absent.
function resolveRuahPreferences(
  intentionData: Record<string, any>,
  prefsColumn?: any
): string[] {
  const existing = intentionData?.ruahPreferences;
  if (Array.isArray(existing) && existing.length > 0) return existing;

  const prefs = prefsColumn || intentionData?.preferences;
  const ruah = Array.isArray(prefs?.ruahSubjects) ? prefs.ruahSubjects : [];
  return ruah
    .map((s: any) => s?.subjectName || s?.subjectId)
    .filter((n: any): n is string => typeof n === 'string' && n.length > 0);
}

// The teacher-submit path and the admin-edit audit snapshot must persist the
// intention body identically, or the audit's before/after would misrepresent
// what is stored. Keep a single source of truth for that JSONB blob.
function buildIntentionsData(intention: TeachingIntention) {
  return {
    department: intention.department,
    currentEmploymentType: intention.currentEmploymentType,
    currentFTE: intention.currentFTE,
    intentionToReturn: intention.intentionToReturn,
    desiredEmploymentStatus: intention.desiredEmploymentStatus,
    targetFTE: intention.targetFTE,
    preferredDays: intention.preferredDays,
    teachingPreferences: intention.teachingPreferences,
    ruahPreferences: intention.ruahPreferences,
    comfortableTeachingAreas: intention.comfortableTeachingAreas,
    leadingLearningRole: intention.leadingLearningRole,
    additionalResponsibilities: intention.additionalResponsibilities,
    additionalComments: intention.additionalComments,
    polPosition: intention.polPosition,
    polMinutes: intention.polMinutes,
    otherAllowances: intention.otherAllowances,
    maxTeachingMinutes: intention.maxTeachingMinutes,
    importSource: intention.importSource,
    // Extended availability and preferences
    availability: intention.availability,
    preferences: intention.preferences,
    // New employment details (structured leave, TIL, special roles)
    employmentDetails: intention.employmentDetails
  };
}

// HR wants a conversation when the teacher marks FTE or day-off as "discuss".
function computeHrDiscussFlag(availability: TeachingIntention['availability']): boolean {
  return (
    availability?.ftePreference === 'discuss' ||
    availability?.dayOffPreference === 'discuss'
  );
}

// v1.6.0: notify the HR leader when a submission is flagged for discussion.
// Fire-and-forget — email failures must not fail the save. The edge function
// enforces idempotency via email_log, so re-saves of an already-emailed
// submission are safely dropped server-side.
function notifyHrDiscussIfFlagged(intention: TeachingIntention, hrDiscussFlag: boolean): void {
  if (!(hrDiscussFlag && intention.status === 'submitted')) return;

  const availability = intention.availability;
  const reasonParts: string[] = [];
  if (availability?.ftePreference === 'discuss') reasonParts.push('FTE / load');
  if (availability?.dayOffPreference === 'discuss') reasonParts.push('day-off preference');
  const reasonSummary = reasonParts.join(' + ') || 'FTE / day-off preferences';

  emailService
    .sendHrDiscussFlag({
      intentionId: intention.id,
      teacherName: intention.staffName,
      teacherStaffCode: intention.staffId,
      department: intention.department || '',
      year: intention.year,
      reasonSummary
    })
    .catch(err => console.error('HR discuss email dispatch failed', err));
}

// Create a service that works with Supabase
export const intentionsService = {
  /**
   * Save a teaching intention to Supabase
   * @param intention The teaching intention to save
   */
  async saveIntention(intention: TeachingIntention): Promise<void> {
    try {
      // Store the complete intention object as JSONB in the intentions field
      const intentionData = {
        department: intention.department,
        currentEmploymentType: intention.currentEmploymentType,
        currentFTE: intention.currentFTE,
        intentionToReturn: intention.intentionToReturn,
        desiredEmploymentStatus: intention.desiredEmploymentStatus,
        targetFTE: intention.targetFTE,
        preferredDays: intention.preferredDays,
        teachingPreferences: intention.teachingPreferences,
        ruahPreferences: intention.ruahPreferences,
        comfortableTeachingAreas: intention.comfortableTeachingAreas,
        leadingLearningRole: intention.leadingLearningRole,
        additionalResponsibilities: intention.additionalResponsibilities,
        additionalComments: intention.additionalComments,
        polPosition: intention.polPosition,
        polMinutes: intention.polMinutes,
        otherAllowances: intention.otherAllowances,
        maxTeachingMinutes: intention.maxTeachingMinutes,
        currentLoad: intention.currentLoad,
        satisfactionScore: intention.satisfactionScore,
        importSource: intention.importSource
      };

      const { error } = await supabase
        .from('teaching_intentions')
        .upsert({
          id: intention.id,
          staff_id: intention.staffId,
          staff_name: intention.staffName,
          year: intention.year,
          intentions: intentionData,
          status: intention.status,
          submitted_at: intention.submittedAt ? new Date(intention.submittedAt).toISOString() : null,
          updated_at: new Date().toISOString()
        });

      if (error) {
        console.error('Error saving intention:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error saving intention:', error);
      throw error;
    }
  },

  /**
   * Get all teaching intentions for a specific academic year
   * @param year The academic year to get intentions for
   * @returns Array of teaching intentions
   */
  async getIntentionsByYear(year: number): Promise<TeachingIntention[]> {
    try {
      const { data, error } = await supabase
        .from('teaching_intentions')
        .select('*')
        .eq('year', year)
        .order('staff_name');

      if (error) {
        console.error('Error getting intentions by year:', error);
        throw error;
      }

      const mapped = data.map(item => {
        // Extract data from the JSONB intentions column
        const intentionData = item.intentions || {};

        return {
          id: item.id,
          staffId: item.staff_id,
          staffName: item.staff_name,
          year: item.year,
          status: item.status,
          periodId: item.period_id,
          submittedAt: item.submitted_at ? new Date(item.submitted_at) : new Date(),
          lastUpdatedAt: item.updated_at ? new Date(item.updated_at) : new Date(),
          // Structured availability/preferences columns. App-submitted rows store
          // their ranked curriculum areas here (separate from the intentions JSONB);
          // imported rows leave these empty.
          availability: item.availability || intentionData.availability,
          preferences: item.preferences || intentionData.preferences,
          employmentDetails: intentionData.employmentDetails,

          // Extract all fields from the intentions JSONB column
          email: '', // Will be filled from staff table when linked
          department: (() => {
            const primary = intentionData.faculty || intentionData.department || 'Unknown';
            const secondary = intentionData.secondaryFaculties;
            if (secondary && secondary.length > 0) {
              return `${primary} • ${secondary.join(' • ')}`;
            }
            return primary;
          })(),
          // Read the coded key first (app rows); fall back to normalising the
          // raw imported label so reports show the real answer, not a default.
          currentEmploymentType: normalizeEmploymentType(intentionData),
          currentFTE: intentionData.currentFTE || 1.0,
          intentionToReturn: normalizeIntentionToReturn(intentionData),
          desiredEmploymentStatus: intentionData.desiredEmploymentStatus || 'full-time',
          targetFTE: intentionData.targetFTE || 1.0,
          preferredDays: intentionData.preferredDays || '',
          teachingPreferences: resolveTeachingPreferences(intentionData, item.preferences),
          ruahPreferences: resolveRuahPreferences(intentionData, item.preferences),
          comfortableTeachingAreas: intentionData.comfortableTeachingAreas || [],
          leadingLearningRole: intentionData.leadingLearningRole || '',
          additionalResponsibilities: intentionData.additionalResponsibilities || '',
          additionalComments: intentionData.additionalComments || '',
          polPosition: intentionData.polPosition || '',
          polMinutes: intentionData.polMinutes || 0,
          otherAllowances: intentionData.otherAllowances || intentionData.otherAllowance || '',
          maxTeachingMinutes: intentionData.maxTeachingMinutes || 0,
          currentLoad: intentionData.currentLoad,
          satisfactionScore: intentionData.satisfactionScore,
          importSource: intentionData.importSource || '',
          
          // Keep intentions for backward compatibility
          intentions: intentionData
        };
      });

      // Populate hrNotes from the private notes table (leadership only; RLS
      // returns nothing for staff so their own notes never surface here).
      return this.attachReviewNotes(mapped);
    } catch (error) {
      console.error('Error getting intentions by year:', error);
      return [];
    }
  },

  /**
   * Get teaching intentions for a specific staff member
   * @param staffId The staff member ID
   * @param year Optional year filter
   * @returns Array of teaching intentions
   */
  async getIntentionsByStaff(staffId: string, year?: number): Promise<TeachingIntention[]> {
    try {
      let query = supabase
        .from('teaching_intentions')
        .select('*')
        .eq('staff_id', staffId)
        .order('year', { ascending: false });

      if (year) {
        query = query.eq('year', year);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error getting intentions by staff:', error);
        throw error;
      }

      return data.map(item => {
        // Extract data from the JSONB intentions column
        const intentionData = item.intentions || {};
        
        return {
          id: item.id,
          staffId: item.staff_id,
          staffName: item.staff_name,
          year: item.year,
          status: item.status,
          submittedAt: item.submitted_at ? new Date(item.submitted_at) : new Date(),
          lastUpdatedAt: item.updated_at ? new Date(item.updated_at) : new Date(),
          // Structured availability/preferences columns. App-submitted rows store
          // their ranked curriculum areas here (separate from the intentions JSONB);
          // imported rows leave these empty.
          availability: item.availability || intentionData.availability,
          preferences: item.preferences || intentionData.preferences,
          employmentDetails: intentionData.employmentDetails,

          // Extract all fields from the intentions JSONB column
          email: '', // Will be filled from staff table when linked
          department: (() => {
            const primary = intentionData.faculty || intentionData.department || 'Unknown';
            const secondary = intentionData.secondaryFaculties;
            if (secondary && secondary.length > 0) {
              return `${primary} • ${secondary.join(' • ')}`;
            }
            return primary;
          })(),
          // Read the coded key first (app rows); fall back to normalising the
          // raw imported label so reports show the real answer, not a default.
          currentEmploymentType: normalizeEmploymentType(intentionData),
          currentFTE: intentionData.currentFTE || 1.0,
          intentionToReturn: normalizeIntentionToReturn(intentionData),
          desiredEmploymentStatus: intentionData.desiredEmploymentStatus || 'full-time',
          targetFTE: intentionData.targetFTE || 1.0,
          preferredDays: intentionData.preferredDays || '',
          teachingPreferences: resolveTeachingPreferences(intentionData, item.preferences),
          ruahPreferences: resolveRuahPreferences(intentionData, item.preferences),
          comfortableTeachingAreas: intentionData.comfortableTeachingAreas || [],
          leadingLearningRole: intentionData.leadingLearningRole || '',
          additionalResponsibilities: intentionData.additionalResponsibilities || '',
          additionalComments: intentionData.additionalComments || '',
          polPosition: intentionData.polPosition || '',
          polMinutes: intentionData.polMinutes || 0,
          otherAllowances: intentionData.otherAllowances || intentionData.otherAllowance || '',
          maxTeachingMinutes: intentionData.maxTeachingMinutes || 0,
          currentLoad: intentionData.currentLoad,
          satisfactionScore: intentionData.satisfactionScore,
          importSource: intentionData.importSource || '',
          
          // Keep intentions for backward compatibility
          intentions: intentionData
        };
      });
    } catch (error) {
      console.error('Error getting intentions by staff:', error);
      return [];
    }
  },

  /**
   * Get a specific teaching intention by ID
   * @param intentionId The intention ID
   * @returns Teaching intention or null if not found
   */
  async getIntentionById(intentionId: string): Promise<TeachingIntention | null> {
    try {
      const { data, error } = await supabase
        .from('teaching_intentions')
        .select('*')
        .eq('id', intentionId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') { // Not found
          return null;
        }
        console.error('Error getting intention by ID:', error);
        throw error;
      }

      // Extract data from the JSONB intentions column
      const intentionData = data.intentions || {};
      
      return {
        id: data.id,
        staffId: data.staff_id,
        staffName: data.staff_name,
        year: data.year,
        status: data.status,
        submittedAt: data.submitted_at ? new Date(data.submitted_at) : new Date(),
        lastUpdatedAt: data.updated_at ? new Date(data.updated_at) : new Date(),
        
        // Extract all fields from the intentions JSONB column
        email: '', // Will be filled from staff table when linked
        department: (() => {
          const primary = intentionData.faculty || intentionData.department || 'Unknown';
          const secondary = intentionData.secondaryFaculties;
          if (secondary && secondary.length > 0) {
            return `${primary} • ${secondary.join(' • ')}`;
          }
          return primary;
        })(),
        currentEmploymentType: intentionData.currentEmploymentType || 'full-time',
        currentFTE: intentionData.currentFTE || 1.0,
        intentionToReturn: intentionData.intentionToReturn || 'uncertain',
        desiredEmploymentStatus: intentionData.desiredEmploymentStatus || 'full-time',
        targetFTE: intentionData.targetFTE || 1.0,
        preferredDays: intentionData.preferredDays || '',
        teachingPreferences: resolveTeachingPreferences(intentionData, data.preferences),
        ruahPreferences: resolveRuahPreferences(intentionData, data.preferences),
        comfortableTeachingAreas: intentionData.comfortableTeachingAreas || [],
        leadingLearningRole: intentionData.leadingLearningRole || '',
        additionalResponsibilities: intentionData.additionalResponsibilities || '',
        additionalComments: intentionData.additionalComments || '',
        polPosition: intentionData.polPosition || '',
        polMinutes: intentionData.polMinutes || 0,
        otherAllowances: intentionData.otherAllowances || '',
        maxTeachingMinutes: intentionData.maxTeachingMinutes || 0,
        currentLoad: intentionData.currentLoad,
        satisfactionScore: intentionData.satisfactionScore,
        importSource: intentionData.importSource || '',
        
        // Keep intentions for backward compatibility
        intentions: intentionData
      };
    } catch (error) {
      console.error('Error getting intention by ID:', error);
      return null;
    }
  },

  /**
   * Update a teaching intention
   * @param intentionId The intention ID
   * @param updates The fields to update
   */
  async updateIntention(intentionId: string, updates: Partial<TeachingIntention>): Promise<void> {
    try {
      const updateData: any = {
        updated_at: new Date().toISOString()
      };

      if (updates.intentions) updateData.intentions = updates.intentions;
      if (updates.status) updateData.status = updates.status;
      if (updates.submittedAt) updateData.submitted_at = new Date(updates.submittedAt).toISOString();
      if (updates.staffName) updateData.staff_name = updates.staffName;

      const { error } = await supabase
        .from('teaching_intentions')
        .update(updateData)
        .eq('id', intentionId);

      if (error) {
        console.error('Error updating intention:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error updating intention:', error);
      throw error;
    }
  },

  /**
   * Delete a teaching intention
   * @param intentionId The intention ID
   */
  async deleteIntention(intentionId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('teaching_intentions')
        .delete()
        .eq('id', intentionId);

      if (error) {
        console.error('Error deleting intention:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error deleting intention:', error);
      throw error;
    }
  },

  /**
   * Bulk save teaching intentions
   * @param intentions Array of teaching intentions to save
   */
  async bulkSaveIntentions(intentions: TeachingIntention[]): Promise<void> {
    try {
      const intentionData = intentions.map(intention => ({
        id: intention.id,
        staff_id: intention.staffId,
        staff_name: intention.staffName,
        year: intention.year,
        status: intention.status,
        submitted_at: intention.submittedAt ? new Date(intention.submittedAt).toISOString() : null,
        intentions: intention.intentions || {},
        updated_at: new Date().toISOString()
      }));

      const { error } = await supabase
        .from('teaching_intentions')
        .upsert(intentionData);

      if (error) {
        console.error('Error bulk saving intentions:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error bulk saving intentions:', error);
      throw error;
    }
  },

  /**
   * Get intentions by status
   * @param status The status to filter by
   * @param year Optional year filter
   * @returns Array of teaching intentions
   */
  async getIntentionsByStatus(status: string, year?: number): Promise<TeachingIntention[]> {
    try {
      let query = supabase
        .from('teaching_intentions')
        .select('*')
        .eq('status', status)
        .order('staff_name');

      if (year) {
        query = query.eq('year', year);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error getting intentions by status:', error);
        throw error;
      }

      return data.map(item => {
        // Extract data from the JSONB intentions column
        const intentionData = item.intentions || {};
        
        return {
          id: item.id,
          staffId: item.staff_id,
          staffName: item.staff_name,
          year: item.year,
          status: item.status,
          submittedAt: item.submitted_at ? new Date(item.submitted_at) : new Date(),
          lastUpdatedAt: item.updated_at ? new Date(item.updated_at) : new Date(),
          // Structured availability/preferences columns. App-submitted rows store
          // their ranked curriculum areas here (separate from the intentions JSONB);
          // imported rows leave these empty.
          availability: item.availability || intentionData.availability,
          preferences: item.preferences || intentionData.preferences,
          employmentDetails: intentionData.employmentDetails,

          // Extract all fields from the intentions JSONB column
          email: '', // Will be filled from staff table when linked
          department: (() => {
            const primary = intentionData.faculty || intentionData.department || 'Unknown';
            const secondary = intentionData.secondaryFaculties;
            if (secondary && secondary.length > 0) {
              return `${primary} • ${secondary.join(' • ')}`;
            }
            return primary;
          })(),
          // Read the coded key first (app rows); fall back to normalising the
          // raw imported label so reports show the real answer, not a default.
          currentEmploymentType: normalizeEmploymentType(intentionData),
          currentFTE: intentionData.currentFTE || 1.0,
          intentionToReturn: normalizeIntentionToReturn(intentionData),
          desiredEmploymentStatus: intentionData.desiredEmploymentStatus || 'full-time',
          targetFTE: intentionData.targetFTE || 1.0,
          preferredDays: intentionData.preferredDays || '',
          teachingPreferences: resolveTeachingPreferences(intentionData, item.preferences),
          ruahPreferences: resolveRuahPreferences(intentionData, item.preferences),
          comfortableTeachingAreas: intentionData.comfortableTeachingAreas || [],
          leadingLearningRole: intentionData.leadingLearningRole || '',
          additionalResponsibilities: intentionData.additionalResponsibilities || '',
          additionalComments: intentionData.additionalComments || '',
          polPosition: intentionData.polPosition || '',
          polMinutes: intentionData.polMinutes || 0,
          otherAllowances: intentionData.otherAllowances || intentionData.otherAllowance || '',
          maxTeachingMinutes: intentionData.maxTeachingMinutes || 0,
          currentLoad: intentionData.currentLoad,
          satisfactionScore: intentionData.satisfactionScore,
          importSource: intentionData.importSource || '',
          
          // Keep intentions for backward compatibility
          intentions: intentionData
        };
      });
    } catch (error) {
      console.error('Error getting intentions by status:', error);
      return [];
    }
  },

  /**
   * Update intention status
   * @param intentionId The intention ID
   * @param status The new status
   */
  async updateIntentionStatus(intentionId: string, status: string): Promise<void> {
    await this.updateIntention(intentionId, { status: status as any });
  },

  /**
   * Bulk import intentions with conflict resolution
   * @param intentions Array of intentions to import
   * @param mode How to handle conflicts ('add', 'replace', 'merge')
   */
  async bulkImportIntentions(intentions: TeachingIntention[], mode: 'add' | 'replace' | 'merge' = 'add'): Promise<void> {
    try {
      const intentionData = intentions.map(intention => ({
        id: intention.id,
        staff_id: intention.staffId,
        staff_name: intention.staffName,
        year: intention.year,
        status: intention.status,
        submitted_at: intention.submittedAt ? new Date(intention.submittedAt).toISOString() : null,
        intentions: intention.intentions || {},
        updated_at: new Date().toISOString()
      }));

      const { error } = await supabase
        .from('teaching_intentions')
        .upsert(intentionData);

      if (error) {
        console.error('Error bulk importing intentions:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error bulk importing intentions:', error);
      throw error;
    }
  },

  /**
   * Get submitted intentions for a year
   * @param year The academic year
   * @returns Array of submitted teaching intentions
   */
  async getSubmittedIntentions(year: number): Promise<TeachingIntention[]> {
    return this.getIntentionsByStatus('submitted', year);
  },

  /**
   * Get draft intentions for a year
   * @param year The academic year
   * @returns Array of draft teaching intentions
   */
  async getDraftIntentions(year: number): Promise<TeachingIntention[]> {
    return this.getIntentionsByStatus('draft', year);
  },

  /**
   * Submit an intention (change status from draft to submitted)
   * @param intentionId The intention ID
   */
  async submitIntention(intentionId: string): Promise<void> {
    await this.updateIntention(intentionId, {
      status: 'submitted',
      submittedAt: new Date()
    });
  },

  /**
   * Revert an intention to draft status
   * @param intentionId The intention ID
   */
  async revertToDraft(intentionId: string): Promise<void> {
    await this.updateIntention(intentionId, {
      status: 'draft'
    });
  },

  // ============================================
  // Period-aware queries
  // ============================================

  /**
   * Get intentions by period ID
   * @param periodId The intention period ID
   * @returns Array of teaching intentions for that period
   */
  async getIntentionsByPeriod(periodId: string): Promise<TeachingIntention[]> {
    try {
      const { data, error } = await supabase
        .from('teaching_intentions')
        .select('*')
        .eq('period_id', periodId)
        .order('staff_name');

      if (error) {
        console.error('Error getting intentions by period:', error);
        throw error;
      }

      // Populate hrNotes from the private notes table (leadership only; RLS
      // returns nothing for staff so their own notes never surface here).
      return this.attachReviewNotes(data.map(item => this.mapDbRowToIntention(item)));
    } catch (error) {
      console.error('Error in getIntentionsByPeriod:', error);
      return [];
    }
  },

  /**
   * Get staff's intention for a specific period
   * @param staffId The staff code
   * @param periodId The intention period ID
   * @returns Teaching intention or null
   */
  async getStaffIntentionForPeriod(staffId: string, periodId: string): Promise<TeachingIntention | null> {
    try {
      const { data, error } = await supabase
        .from('teaching_intentions')
        .select('*')
        .eq('staff_id', staffId)
        .eq('period_id', periodId)
        .maybeSingle();

      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        console.error('Error getting staff intention for period:', error);
        throw error;
      }

      return data ? this.mapDbRowToIntention(data) : null;
    } catch (error) {
      console.error('Error in getStaffIntentionForPeriod:', error);
      return null;
    }
  },

  /**
   * Create or update an intention for a period
   * @param intention The teaching intention data
   * @param periodId The intention period ID
   */
  async saveIntentionForPeriod(intention: TeachingIntention, periodId: string): Promise<void> {
    try {
      const hrDiscussFlag = computeHrDiscussFlag(intention.availability);

      // Compute is_late:
      //  - Only evaluated when the incoming status is 'submitted' (first submit or re-submit).
      //  - Preserve existing is_late if already true — never flip back to false.
      //  - Otherwise compare submitted_at vs the period's due_date.
      let isLate = false;
      if (intention.status === 'submitted') {
        // Preserve existing late flag from prior row if it exists
        const { data: existing } = await supabase
          .from('teaching_intentions')
          .select('is_late, submitted_at')
          .eq('id', intention.id)
          .maybeSingle();

        if (existing?.is_late === true) {
          isLate = true;
        } else if (!existing?.submitted_at) {
          // First submission — check due_date
          const { data: period } = await supabase
            .from('intention_periods')
            .select('due_date')
            .eq('id', periodId)
            .maybeSingle();

          if (period?.due_date) {
            const dueAt = new Date(period.due_date).getTime();
            const nowAt = new Date(intention.submittedAt || new Date()).getTime();
            isLate = nowAt > dueAt;
          }
        }
        // else: re-submitting a previously on-time row — keep isLate false
      }

      const intentionData = buildIntentionsData(intention);

      const { error } = await supabase
        .from('teaching_intentions')
        .upsert({
          id: intention.id,
          staff_id: intention.staffId,
          staff_name: intention.staffName,
          year: intention.year,
          period_id: periodId,
          intentions: intentionData,
          availability: intention.availability || {},
          preferences: intention.preferences || {},
          status: intention.status,
          submitted_at: intention.submittedAt ? new Date(intention.submittedAt).toISOString() : null,
          updated_at: new Date().toISOString(),
          is_late: isLate,
          hr_discuss_flag: hrDiscussFlag,
          entered_by_staff_id: intention.enteredByStaffId ?? null
        });

      if (error) {
        console.error('Error saving intention for period:', error);
        throw error;
      }

      notifyHrDiscussIfFlagged(intention, hrDiscussFlag);
    } catch (error) {
      console.error('Error in saveIntentionForPeriod:', error);
      throw error;
    }
  },

  /**
   * Update an existing intention on behalf of a teacher (admin / assistant_principal
   * / HR reviewer), preserving its review status and its own submission period.
   *
   * The intention UPDATE and its audit_logs INSERT run together inside the
   * update_intention_with_audit RPC (a single transaction), so a failed audit can
   * never leave a committed-but-unaudited edit (issue #67 §1). The RPC is SECURITY
   * INVOKER, so the caller's own RLS still gates the write, and it takes the audit
   * actor email from the verified JWT server-side (no client-supplied email).
   *
   * `actor` is the REAL authenticated user (unwrap impersonation via realActor()
   * before calling); only its staffCode is used here, to stamp entered_by_staff_id.
   */
  async updateIntentionByAdmin(
    intention: TeachingIntention,
    periodId: string,
    actor: { email?: string; staffCode?: string }
  ): Promise<void> {
    if (!periodId) {
      throw new Error('Cannot save: this intention has no submission period.');
    }

    // Pre-edit snapshot for the audit trail — captured in the SAME shape as
    // new_values below so the before/after are directly diff-comparable (#67 §2).
    const { data: before } = await supabase
      .from('teaching_intentions')
      .select('status, intentions, availability, preferences')
      .eq('id', intention.id)
      .maybeSingle();

    // Stamp who made the change.
    const toSave: TeachingIntention = {
      ...intention,
      enteredByStaffId: actor.staffCode ?? intention.enteredByStaffId
    };

    const hrDiscussFlag = computeHrDiscussFlag(toSave.availability);
    const intentionsData = buildIntentionsData(toSave);
    const newValues = {
      status: toSave.status,
      intentions: intentionsData,
      availability: toSave.availability ?? {},
      preferences: toSave.preferences ?? {}
    };

    // Atomic: the targeted intention UPDATE and the audit INSERT succeed or fail
    // together. Deliberately does NOT touch status / submitted_at / is_late /
    // identity columns — an admin edit preserves the submission's review status
    // and its own period.
    const { error } = await supabase.rpc('update_intention_with_audit', {
      p_intention_id: intention.id,
      p_intentions: intentionsData,
      p_availability: toSave.availability ?? {},
      p_preferences: toSave.preferences ?? {},
      p_hr_discuss_flag: hrDiscussFlag,
      p_entered_by_staff_id: toSave.enteredByStaffId ?? null,
      p_old_values: before ?? null,
      p_new_values: newValues
    });

    if (error) {
      // Atomic RPC — on failure nothing was persisted (the edit is rolled back
      // with the audit). Safe to ask the user to retry.
      console.error('Atomic intention edit + audit failed:', error);
      throw new Error(
        'Could not save your changes — nothing was updated. Please try again, and report this if it keeps happening.'
      );
    }

    // Side effect only after the write is durably committed.
    notifyHrDiscussIfFlagged(toSave, hrDiscussFlag);
  },

  // ============================================
  // HR Review methods
  // ============================================

  /**
   * Get all HR-approved intentions for an academic year
   * These are the intentions that should flow into the grid preference system
   * @param year The academic year
   * @returns Array of HR-approved teaching intentions
   */
  async getApprovedIntentionsByYear(year: number): Promise<TeachingIntention[]> {
    try {
      const { data, error } = await supabase
        .from('teaching_intentions')
        .select('*')
        .eq('year', year)
        .eq('status', 'hr_approved')
        .order('staff_name');

      if (error) {
        console.error('Error getting approved intentions by year:', error);
        throw error;
      }

      return data.map(item => this.mapDbRowToIntention(item));
    } catch (error) {
      console.error('Error in getApprovedIntentionsByYear:', error);
      return [];
    }
  },

  /**
   * Approve an intention (HR review)
   * @param intentionId The intention ID
   * @param reviewedBy The staff ID of the reviewer
   * @param notes Optional notes from reviewer
   */
  async approveIntention(intentionId: string, reviewedBy: string, notes?: string): Promise<void> {
    try {
      // hr_reviewed_by is a FK to staff(id). The UI passes the auth uid, which
      // only equals staff.id for some users — resolve the real staff.id here so
      // the write doesn't fail the FK for everyone else (issue #72).
      const reviewerStaffId = await this.resolveReviewerStaffId(reviewedBy);

      // Save the private review note first (see setReviewNote). If this fails the
      // status update below won't run, so we never record an approval whose note
      // silently vanished. Notes are optional on approve.
      await this.setReviewNote(intentionId, reviewerStaffId ?? reviewedBy, notes ?? '');

      const { error } = await supabase
        .from('teaching_intentions')
        .update({
          status: 'hr_approved',
          hr_reviewed_by: reviewerStaffId,
          hr_reviewed_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', intentionId);

      if (error) {
        console.error('Error approving intention:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in approveIntention:', error);
      throw error;
    }
  },

  /**
   * Flag an intention for discussion (HR review)
   * @param intentionId The intention ID
   * @param reviewedBy The staff ID of the reviewer
   * @param notes Required notes explaining why flagged
   */
  async flagIntention(intentionId: string, reviewedBy: string, notes: string): Promise<void> {
    try {
      // Resolve the reviewer's staff.id for the hr_reviewed_by FK (issue #72).
      const reviewerStaffId = await this.resolveReviewerStaffId(reviewedBy);

      // Save the private review note first (flagging requires a note explaining
      // why). If it fails we don't flag, so the reason is never lost.
      await this.setReviewNote(intentionId, reviewerStaffId ?? reviewedBy, notes);

      const { error } = await supabase
        .from('teaching_intentions')
        .update({
          status: 'hr_flagged',
          hr_reviewed_by: reviewerStaffId,
          hr_reviewed_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', intentionId);

      if (error) {
        console.error('Error flagging intention:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in flagIntention:', error);
      throw error;
    }
  },

  /**
   * Resolve a reviewer's staff.id (the primary key) from whatever identifier the
   * UI passes as `reviewedBy`.
   *
   * `teaching_intentions.hr_reviewed_by` is a FK to staff(id), but the app's
   * `user.id` is the Supabase auth uid, which only equals staff.id for users
   * whose PK was set to their auth uid. For everyone else (e.g. staff linked via
   * auth_user_id) the auth uid is not a valid staff.id and the write fails the FK
   * (issue #72). During impersonation `reviewedBy` is already the target's
   * staff.id, which this also handles (id.eq match).
   *
   * Returns null when no staff row matches, so callers can write null rather than
   * violate the FK.
   */
  async resolveReviewerStaffId(reviewedBy: string): Promise<string | null> {
    // Both id and auth_user_id hold UUIDs; guard so a malformed value can't be
    // interpolated into the PostgREST .or() filter below.
    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(reviewedBy || '');
    if (!isUuid) return null;
    const { data, error } = await supabase
      .from('staff')
      .select('id')
      .or(`id.eq.${reviewedBy},auth_user_id.eq.${reviewedBy}`)
      .limit(1);
    if (error) {
      console.error('Error resolving reviewer staff id:', error);
      return null;
    }
    return data?.[0]?.id ?? null;
  },

  /**
   * Save (or clear) the private HR review note for an intention.
   *
   * Notes live in intention_review_notes, NOT on teaching_intentions, so the
   * staff member the intention belongs to can never read them (RLS denies
   * teachers all access to that table). One note per intention; writing again
   * overwrites it. Passing an empty note removes it.
   *
   * @param intentionId The intention the note belongs to
   * @param reviewerId  The staff_id of the reviewer (recorded as created_by)
   * @param note        The note text; empty/whitespace clears any existing note
   */
  async setReviewNote(intentionId: string, reviewerId: string, note: string): Promise<void> {
    const trimmed = (note ?? '').trim();

    if (!trimmed) {
      const { error } = await supabase
        .from('intention_review_notes')
        .delete()
        .eq('intention_id', intentionId);
      if (error) {
        console.error('Error clearing review note:', error);
        throw error;
      }
      return;
    }

    const { error } = await supabase
      .from('intention_review_notes')
      .upsert(
        { intention_id: intentionId, note: trimmed, created_by: reviewerId },
        // Tenant-scoped unique index: (tenant_id, intention_id).
        { onConflict: 'tenant_id,intention_id' }
      );
    if (error) {
      console.error('Error saving review note:', error);
      throw error;
    }
  },

  /**
   * Attach private review notes to a list of intentions (populates hrNotes).
   *
   * Runs one batched query against intention_review_notes. RLS means only
   * leadership get rows back; for everyone else the query returns nothing and
   * hrNotes stays undefined — so this is safe to call on any code path without
   * leaking notes to staff.
   */
  async attachReviewNotes<T extends { id: string; hrNotes?: string }>(intentions: T[]): Promise<T[]> {
    if (!intentions.length) return intentions;

    const { data, error } = await supabase
      .from('intention_review_notes')
      .select('intention_id, note')
      .in('intention_id', intentions.map(i => i.id));

    if (error) {
      // Don't fail the whole list load just because notes couldn't be read.
      console.error('Error loading review notes:', error);
      return intentions;
    }

    const noteByIntention = new Map<string, string>(
      (data ?? []).map((r: { intention_id: string; note: string }) => [r.intention_id, r.note])
    );

    return intentions.map(i => ({ ...i, hrNotes: noteByIntention.get(i.id) ?? undefined }));
  },

  /**
   * Bulk approve multiple intentions
   * @param intentionIds Array of intention IDs to approve
   * @param reviewedBy The staff ID of the reviewer
   */
  async bulkApproveIntentions(intentionIds: string[], reviewedBy: string): Promise<{ success: number; failed: number }> {
    let success = 0;
    let failed = 0;

    for (const id of intentionIds) {
      try {
        await this.approveIntention(id, reviewedBy);
        success++;
      } catch (error) {
        console.error(`Failed to approve intention ${id}:`, error);
        failed++;
      }
    }

    return { success, failed };
  },

  /**
   * Get intentions pending HR review for a period
   * @param periodId The intention period ID
   * @returns Array of submitted intentions awaiting review
   */
  async getPendingReviewIntentions(periodId: string): Promise<TeachingIntention[]> {
    try {
      const { data, error } = await supabase
        .from('teaching_intentions')
        .select('*')
        .eq('period_id', periodId)
        .eq('status', 'submitted')
        .order('staff_name');

      if (error) {
        console.error('Error getting pending review intentions:', error);
        throw error;
      }

      return data.map(item => this.mapDbRowToIntention(item));
    } catch (error) {
      console.error('Error in getPendingReviewIntentions:', error);
      return [];
    }
  },

  /**
   * Get intentions by HR status for a period
   * @param periodId The intention period ID
   * @param status The HR status to filter by
   * @returns Array of teaching intentions
   */
  async getIntentionsByHRStatus(periodId: string, status: 'hr_approved' | 'hr_flagged' | 'submitted'): Promise<TeachingIntention[]> {
    try {
      const { data, error } = await supabase
        .from('teaching_intentions')
        .select('*')
        .eq('period_id', periodId)
        .eq('status', status)
        .order('staff_name');

      if (error) {
        console.error('Error getting intentions by HR status:', error);
        throw error;
      }

      return data.map(item => this.mapDbRowToIntention(item));
    } catch (error) {
      console.error('Error in getIntentionsByHRStatus:', error);
      return [];
    }
  },

  // ============================================
  // Helper methods
  // ============================================

  /**
   * Map a database row to a TeachingIntention object
   */
  mapDbRowToIntention(item: any): TeachingIntention {
    const intentionData = item.intentions || {};

    return {
      id: item.id,
      staffId: item.staff_id,
      staffName: item.staff_name,
      year: item.year,
      status: item.status,
      submittedAt: item.submitted_at ? new Date(item.submitted_at) : new Date(),
      lastUpdatedAt: item.updated_at ? new Date(item.updated_at) : new Date(),

      // Period reference
      periodId: item.period_id,

      // HR review fields. hrNotes is NOT stored on this row — it lives in the
      // private intention_review_notes table and is populated separately by
      // attachReviewNotes() for the screens that display it (leadership only).
      hrReviewedBy: item.hr_reviewed_by,
      hrReviewedAt: item.hr_reviewed_at ? new Date(item.hr_reviewed_at) : undefined,
      hrNotes: undefined,

      // v1.2.0 timing + routing flags
      isLate: item.is_late === true,
      hrDiscussFlag: item.hr_discuss_flag === true,
      enteredByStaffId: item.entered_by_staff_id || undefined,

      // Extended availability and preferences from dedicated columns
      availability: item.availability || intentionData.availability,
      preferences: item.preferences || intentionData.preferences,

      // Employment details (new field for structured leave, TIL, special roles)
      employmentDetails: intentionData.employmentDetails,

      // Extract all fields from the intentions JSONB column
      email: '',
      department: (() => {
        const primary = intentionData.faculty || intentionData.department || 'Unknown';
        const secondary = intentionData.secondaryFaculties;
        if (secondary && secondary.length > 0) {
          return `${primary} • ${secondary.join(' • ')}`;
        }
        return primary;
      })(),
      currentEmploymentType: intentionData.currentEmploymentType || 'full-time',
      currentFTE: intentionData.currentFTE || 1.0,
      intentionToReturn: intentionData.intentionToReturn || 'uncertain',
      desiredEmploymentStatus: intentionData.desiredEmploymentStatus || 'full-time',
      targetFTE: intentionData.targetFTE || 1.0,
      preferredDays: intentionData.preferredDays || '',
      teachingPreferences: resolveTeachingPreferences(intentionData, item.preferences),
      ruahPreferences: resolveRuahPreferences(intentionData, item.preferences),
      comfortableTeachingAreas: intentionData.comfortableTeachingAreas || [],
      leadingLearningRole: intentionData.leadingLearningRole || '',
      additionalResponsibilities: intentionData.additionalResponsibilities || '',
      additionalComments: intentionData.additionalComments || '',
      polPosition: intentionData.polPosition || '',
      polMinutes: intentionData.polMinutes || 0,
      otherAllowances: intentionData.otherAllowances || '',
      maxTeachingMinutes: intentionData.maxTeachingMinutes || 0,
      currentLoad: intentionData.currentLoad,
      satisfactionScore: intentionData.satisfactionScore,
      importSource: intentionData.importSource || ''
    };
  }
};

// For backward compatibility, also export the service factory function
export const createIntentionsService = (database?: any) => {
  // Ignore the database parameter since we're using Supabase directly
  return intentionsService;
};